import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import Back from '../../../components/common/Back';
import { Upload } from "@aws-sdk/lib-storage";
import { s3Client } from "./s3-client-config";
import Layout from '../Layout';
import { fetchWeightTrackerData, deleteWeightEntry, addWeight, clearMessage, removeNotUploadedImages } from '../../../redux/slices/customer/weightTrackerSlice';
import 'bootstrap-icons/font/bootstrap-icons.css';
import chooseIcon from '../../../images/icon-choose-img.png';
import showAlert from '../../../components/common/showAlert';

import { showLoader, hideLoader } from '../../../redux/loadingSlice';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const WeightTracker = () => {
  const dispatch = useDispatch();

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const { weights, currentWeekAverage } = useSelector(state => state.weightTracker);
  const [selectedDate, setSelectedDate] = useState(getCurrentDate()); 
  const [updatedWeights, setUpdatedWeights] = useState(weights);
  const [displayCount, setDisplayCount] = useState(6);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [weight, setWeight] = useState('');
  const [image, setImage] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);



  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    dispatch(fetchWeightTrackerData());
  }, [dispatch]); 

  useEffect(() => {
    dispatch(clearMessage());
  }, []); 

  const fetchFilteredData = () => {
    const filters = { selectedPeriod, startDate, endDate };
    dispatch(fetchWeightTrackerData(filters));
  };
 
  useEffect(() => {
     fetchFilteredData();
  }, [dispatch, selectedPeriod, startDate, endDate]);

  const handlePeriodChange = (event) => { setSelectedPeriod(event.target.value); setStartDate(''); setEndDate(''); }

  const handleStartDateChange = (event) => setStartDate(event.target.value);
  const handleEndDateChange = (event) => setEndDate(event.target.value);

  useEffect(() => {
    setUpdatedWeights(weights);
  }, [weights]);

  const chartData = useMemo(() => {
    const labels = updatedWeights.map(entry => entry.check_in_date).reverse();
    const data1 = updatedWeights.map(entry => entry.weight).reverse();
    const data2 = data1.map((value, index) => data1[index] - 5);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Weight Line 1',
          data: data2,
          borderWidth: 1,
          pointRadius: 0,
          pointBackgroundColor: '#f4dadb',
          pointStyle: 'images/point.png',
          fill: true,
          borderColor: ["#f4dadb"],
          backgroundColor: '#f4dadb'
        },
        {
          label: '',
          data: data1,
          borderWidth: 3,
          pointStyle: "images/point.png",
          pointRadius: 5,
          pointBackgroundColor: "red",
          labelColor: "green",
          borderColor: ["red"],
          fill: true,
          backgroundColor: '#f8ecec'
        },
      ],
    };
  }, [updatedWeights]);

  const options = useMemo(() => ({
    responsive: true,
    scales: {
      y: {
        title: { display: true, text: 'Weight (Kg)' },
        ticks: { beginAtZero: false },
      },
    },
    plugins: {
      legend: { display: true, labels: { color: '#1ea0f7' } },
    },
  }), []);

  const handleDelete = (id, index) => {
    const newWeights = updatedWeights.filter((_, i) => i !== index);

    dispatch(deleteWeightEntry(id)).then(() => {
      dispatch(fetchWeightTrackerData());
    });
  };

  const handleLoadMore = () => setDisplayCount(prevCount => prevCount + 6);
  const handleDateChange = (event) => setSelectedDate(event.target.value);
  const handleWeightChange = (event) => setWeight(event.target.value);
  const handleImageChange = (event) => setImage(event.target.files[0]);
  const handleChooseImageClick = () => document.getElementById('imageInput').click();


const handleFormSubmitOr = async (event) => {
  event.preventDefault();
  
  showLoader();
  
  if (!weight) {
    showAlert("error", "Please enter weight");
    return;
  }

  try {
    const uploadedUrls = [];

    if (images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        const params = {
          Bucket: process.env.REACT_APP_AWS_BUCKET || "alex-gamble-coaching-bucket",
          Key: `${Date.now()}_${file.name}`,
          Body: file,
          ContentType: file.type,
        };

        const parallelUpload = new Upload({
          client: s3Client,
          params,
        });

        await parallelUpload.done();

        const url = `https://${params.Bucket}.s3.${process.env.REACT_APP_AWS_DEFAULT_REGION || "ap-southeast-2"}.amazonaws.com/${params.Key}`;
        uploadedUrls.push(url);
      }
    }

    const payload = {
      weight_date: selectedDate,
      weight: weight,
      files_url: uploadedUrls || [],
    };

    await dispatch(addWeight(payload));
    await dispatch(fetchWeightTrackerData());

    setWeight("");
    setImages([]);
    closeModal();

    showAlert("success", `Entry successfully added${uploadedUrls.length ? ` along with ${uploadedUrls.length} image(s)` : ""}`);
  } catch (error) {
    console.error("Upload or submit failed:", error);
    showAlert("error", "Failed to upload images or submit form.");
  }
};





  const handleFormSubmitold = (event) => {
  event.preventDefault();

  const formData = new FormData();
    formData.append("weight_date", selectedDate);
    formData.append("weight", weight);

    // Append multiple images to formData
    images.forEach((image, index) => {
      formData.append(`filesToUpload[${index}]`, image);
    });

   
    // Dispatch the action with the prepared FormData
    dispatch(addWeight(formData))
      .then(() => dispatch(fetchWeightTrackerData())) // Fetch updated weight data
      .finally(() => {
        setWeight(""); // Reset weight
        setImages([]); // Reset images
        closeModal(); // Close modal
         
        showAlert('success', 'Entry successfully added along with '+images.length+' image(s)');
      });
  };

const handleFormSubmit = async (event) => {
  event.preventDefault();

  const fileData = images.map((image) => ({
    file_name: image.name,
    file_type: image.type,
  }));

  const payload = {
    weight_date: selectedDate,
    weight: weight,
    files: fileData,
  };

  try {
    // 1. Get the presigned URLs and image IDs
    const response = await dispatch(addWeight(payload)).unwrap();

    const uploaded = [];
    const not_uploaded = [];

    showLoader();
    // 2. Upload each file to S3 using the presigned URL
    await Promise.all(
      response.map(async (fileMeta) => {
        const imageFile = images.find((img) => img.name === fileMeta.file_name);
        if (!imageFile) return;

        try {
          const uploadResponse = await fetch(fileMeta.presigned_url, {
            method: "PUT",
            headers: {
              "Content-Type": imageFile.type,
              "x-amz-acl": "public-read",
            },
            body: imageFile,
          });

          if (uploadResponse.ok) {
            uploaded.push(fileMeta.image_id);
          } else {
            not_uploaded.push(fileMeta.image_id);
          }
        } catch (err) {
          not_uploaded.push(fileMeta.image_id);
        }
      })
    );

    // Log or handle uploaded and not_uploaded arrays
    console.log("Uploaded IDs:", uploaded);
    console.log("Not Uploaded IDs:", not_uploaded);
    
    setWeight("");
    setImages([]);
    closeModal();

    showAlert("success", `Entry successfully added.`);

    if (not_uploaded.length > 0) {
    	dispatch(removeNotUploadedImages({'not_uploaded':not_uploaded}));
    }    
    await dispatch(fetchWeightTrackerData());

  } catch (error) {
    console.error("Upload error:", error);
    showAlert("error", "Failed to upload images.");
  }
};




const [isImageModalOpen, setIsImageModalOpen] = useState(false); // state for image modal
const [selectedImage, setSelectedImage] = useState(null); // state for selected image URL
const [selected_images_urls, set_selected_images_urls] = useState([]);

const openImageModal = (imageUrls) => {
  setCurrentImageIndex(0);
  set_selected_images_urls(imageUrls);
  setSelectedImage(imageUrls[0]);
  setIsImageModalOpen(true);

};

const closeImageModal = () => {
  setIsImageModalOpen(false);
  setSelectedImage(null);
  setCurrentImageIndex(0);
};

 const showNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === selected_images_urls.length - 1 ? 0 : prevIndex + 1
    );
  };

  const showPreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? selected_images_urls.length - 1 : prevIndex - 1
    );
  };

  return (
    <Layout>
      <main className="main">
        <header className="main-head flex justify-between align-center">
          <h1 className="greet-user-text">Weight Tracker</h1>
          <div className="ms-auto">
            <Back />
          </div>
        </header>
        <div className="main-content">
          <div className="main-content_left w-100">
            <div className="chart">

              <div className="tracking-options d-flex flex-wrap align-items-center mb-4">
                {/* Select box for Period */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <select value={selectedPeriod} onChange={handlePeriodChange} className="form-select w-100">
                    <option value="All">All</option>
                    <option value="ThisWeek">This Week</option>
                    <option value="ThisMonth">This Month</option>
                    <option value="Last3Months">Last 3 Months</option>
                    <option value="Last6Months">Last 6 Months</option>
                    <option value="ThisYear">This Year</option>
                  </select>
                </div>

                {/* Calendar picker for Start Date */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className="form-select w-100"
                    placeholder="Start Date"
                  />
                </div>

                {/* Calendar picker for End Date */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className="form-select w-100"
                    placeholder="End Date"
                  />
                </div>

                {/* Step Tracker Button */}
                <div className="flex-fill mb-2 mb-md-0 me-2">
                  <Link className="btn-curve tracking_links w-100" to="/step-tracker">
                      Step Tracker
                  </Link>
                </div>
              </div>


              {/* Render the Line chart with the data and options */}
              <Line data={chartData} options={options} />
            </div>
          </div>
          <div id="content-right" className="main-content_right w-100 sec-wg-card pb-3">
            <div className="mt-3">
              <header className="flex align-center justify-between">
                <h3>WEIGHT GOAL</h3>
              </header>
              <div className="wg-wrap">
                <div className="card-wg">
                  <section className="today-stats mt-3">
                    <div className="goal flex align-center justify-between">
                      <div>
                        <p>Current</p>
                        <h2>{currentWeekAverage} KG</h2>
                      </div>
                      <div>
                        <p>Goal</p>
                        <h2> KG</h2>
                      </div>
                    </div>
                    <div className="dashed-bordered">
                      <span className="dot left"></span>
                      <span className="dot right"></span>
                    </div>
                  </section>
                  <section className="calorie-breakdown mt-3">
                    <div>
                      <button className="btn-curve btn-green d-block w-100" onClick={openModal}>Weight Check-in</button>
                    </div>
                  </section>
                </div>
                <div className="card-curve-white mt-3">
                  <section>
                    <div className="text-center">
                      <p>Current Week Average Weight</p>
                      <h3 className="mt-2">{currentWeekAverage} KG</h3>
                    </div>
                  </section>
                  <div className="mt-3">
                    <Link className="btn-curve" to="/previous-weeks-average-weights">
                      View Previous Week's Average
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-entries mt-3">
              <header className="text-center">
                <h3>ENTRIES</h3>
              </header>
              <div className="entries-wrap mt-3">
                {/* Entries list */}
                {updatedWeights.slice(0, displayCount).map((entry, index) => (
                  <div className="entries-item" key={index} style={{ display: 'block' }}>
                    <header>
                      <div className="delete-entry" onClick={() => handleDelete(entry.id, index)}>
                        <i className="bi bi-trash3 cursor-pointer"></i>
                      </div>
                    
                      {entry.images_urls && entry.images_urls.length > 0 && (
  <img
    src={entry.images_urls[0]}
    alt="Weight image"
    style={{
      width: '45px',
      height: '45px',
      borderRadius: '50%',
      position: 'absolute',
      left: '1px',
      top: '50%',
      transform: 'translateY(-50%)'
    }}
    onClick={() => openImageModal(entry.images_urls)}
  />
)}

    
                      <p>{entry.check_in_date}</p>
                      <div className="weight-delete">{entry.weight} kg</div>
                    </header>
                  </div>
                ))}
              </div>
              {updatedWeights.length > displayCount && (
                <div className="text-center mt-3 load-more-container">
                  <button className="btn-curve" onClick={handleLoadMore}>
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>





     {isModalOpen && (
        <div id="weightModal" className="weightmodal modal-weight-checkin onmodal">
          <div className="modal-container" id="videoModalinner">
             <button
              type="button"
              style={{position:'absolute', right:'10px', marginTop:'5px', zIndex:'9999999'}}
              className="btn-close"
              aria-label="Close"
              onClick={closeModal} // Close modal on click
            />

            <div className="modal-body">
              <form onSubmit={handleFormSubmit} encType="multipart/form-data">
                <div className="weight-check-in">
                  <header className="flex align-center gap-m">
                    <h4>Weight Check-In</h4>
                    <div className="ms-auto">
                      <div className="icon-calendar-wrap">
                        <input
                          name="weight_date"
                          id="weight_date"
                          className="icon-calendar-wc"
                          type="date"
                          value={selectedDate}
                          onChange={handleDateChange}
                        />
                      </div>
                    </div>
                  </header>
                  <div className="w-100 mt-4">
                    <label className="inputLabel mb-2" htmlFor="weight">
                      Weight (KG)
                    </label>
                    <div className="flex align-start justify-between gap-sm">
                      <div className="input-weight-wrap">
                        <div className="flex align-items-center">
                          <input
                            className="form-input-weightkg modal-btn-item"
                            type="number"
                            step="0.01"
                            name="weight"
                            id="weight"
                            placeholder="Weight"
                            required
                            value={weight}
                            onChange={handleWeightChange}
                          />
                        </div>
                      </div>
                      <div>
                        <input
                        type="file"
                        name="filesToUpload[]"
                        id="imageInput"
                        multiple="multiple"
                        style={{ display: 'none' }}
                        onChange={(e) => setImages(Array.from(e.target.files))}
                      />
                      <button
                        type="button"
                        id="addImageButton"
                        className="modal-btn-item flex align-center justify-center btn-choose-img px-3 py-3 rounded"
                        onClick={handleChooseImageClick}
                      >
                        <span className="pe-3">Choose Images</span>
                        <img src={chooseIcon} alt="Choose" />
                      </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group mb-0 mt-4">
                    <button type="submit" className="modal-btn-item btn-curve">
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


  {isImageModalOpen && (
  <div
    className="image-modal-overlay"
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 9999,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
    onClick={closeImageModal} // Close modal on overlay click
  >
    <div
      className="image-modal-content"
      style={{
        position: 'relative',
        backgroundColor: 'transparent',
        borderRadius: '10px',
        maxWidth: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()} // Prevent closing modal when clicking inside the content
    >
      {/* Close Button */}
      <button
        type="button"
        className="btn-close"
        style={{
          position: 'absolute',
          right: '10px',
          top: '10px',
          background: 'white',
          border: 'none',
          fontSize: '30px',
          color: 'black',
          zIndex: 10000,
          borderRadius: '50%',
          padding: '4px 8px 13px 8px',
          cursor: 'pointer',
        }}
        onClick={closeImageModal}
      >
        &times;
      </button>

      {selected_images_urls.length > 1 && (
  <button
    type="button"
    onClick={showPreviousImage}
    style={{
      position: 'absolute',
      left: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'white',
      border: 'none',
      borderRadius: '50%',
      fontSize: '30px',
      padding: '0px 15px 0px 15px',
      cursor: 'pointer',
      zIndex: 1000,
    }}
  >
    &#8249;
  </button>
)}

      {/* Image Display */}
      <img
        src={selected_images_urls[currentImageIndex]}
        alt="Large preview"
        style={{
          width: '100%',
          maxHeight: '80vh',
          objectFit: 'contain',
        }}
      />

{selected_images_urls.length > 1 && (
   
      <button
        type="button"
        onClick={showNextImage}
        style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'white',
          border: 'none',
          borderRadius: '50%',
          fontSize: '30px',
          padding: '0px 15px 0px 15px',
          cursor: 'pointer',
          zIndex: 1000,
        }}
      >
        &#8250;
      </button>
      )}
    </div>
  </div>
)}










    </Layout>
  );
};

export default WeightTracker;